import { useEffect } from "react"
import { useParams } from "react-router-dom"
import { useApp } from "../AppProvider"
import { ErrorMessage } from "../components/common/Feedback/ErrorMessage"
import { LoadingMessage } from "../components/common/Feedback/LoadingMessage"
import { useResolveShareToken } from "../hooks/useShareLinks"
import { LabReportPage } from "./LabReportDetails"
import { PatientDetailsPage } from "./PatientDetails"

export const AnonymousSharePage = () => {
  const { token } = useParams<{ token: string }>()
  const { setShareData, setIsAnonymous } = useApp()
  const { data: shareData, isLoading, error } = useResolveShareToken(token || "")

  // Set up share context when data is loaded
  useEffect(() => {
    if (shareData && token) {
      setShareData({
        shareToken: token,
        ...shareData,
      })
      setIsAnonymous(true)
    }

    // Cleanup when leaving anonymous page
    return () => {
      setShareData(undefined)
      setIsAnonymous(false)
    }
  }, [shareData, token, setShareData, setIsAnonymous])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md">
          <LoadingMessage />
        </div>
      </div>
    )
  }

  if (error || !shareData) {
    const errorMessage = error instanceof Error ? error.message : "No share token provided"
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md">
          <ErrorMessage message={errorMessage} />
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen bg-gray-50">
      {/* Floating toast in top right */}
      <div className="fixed top-4 right-4 z-50 max-w-sm">
        <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
          <a
            className="flex items-center gap-2"
            href="https://turbodvm.com/home"
            target="_blank"
            rel="noopener noreferrer">
            <img src="/icon.png" alt="TurboDVM" className="h-4 w-4" />
            <span className="text-xs text-gray-500">
              Powered by{" "}
              <span className="text-primary-600 hover:text-primary-700 text-xs font-medium">TurboDVM</span>
            </span>
          </a>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <AnonymousPageContent
          resourceType={shareData.resourceType}
          resourceId={shareData.resourceId}
          organizationId={shareData.organizationId}
        />
      </div>
    </div>
  )
}

interface AnonymousPageContentProps {
  resourceType: string
  resourceId: string
  organizationId: string
}

const AnonymousPageContent = ({ resourceType, resourceId, organizationId }: AnonymousPageContentProps) => {
  switch (resourceType) {
    case "lab-report":
      return <LabReportPage />

    case "patient":
      return <PatientDetailsPage />

    default:
      return <ErrorMessage message={`Unsupported content type: ${resourceType}`} />
  }
}
